/**
 * DialHoop Image Utilities
 *
 * compressImage(file, options) → Promise<{ dataUrl, sizeKB, compressed }>
 *
 * Strategy:
 *   1. Draw image onto an offscreen canvas
 *   2. Scale down if dimensions exceed maxWidth/maxHeight
 *   3. Encode as JPEG at starting quality
 *   4. If output exceeds targetSizeKB, binary-search for a lower quality
 *   5. Return base64 dataURL
 *
 * Why canvas instead of a library?
 *   Zero dependencies, works in every browser, no bundle size cost.
 */

const DEFAULT_OPTIONS = {
  maxWidth:     800,    // px — profile photos don't need to be wider
  maxHeight:    800,    // px
  targetSizeKB: 150,    // aim for under 150 KB (well inside Firestore 1MB doc limit)
  maxSizeKB:    200,    // hard ceiling — reject if we still can't get below this
  startQuality: 0.85,   // first attempt quality
  minQuality:   0.30,   // don't go lower than this to keep it recognisable
};

/**
 * Load a File into an HTMLImageElement
 */
function loadImage(file) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload  = () => { URL.revokeObjectURL(url); resolve(img); };
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error("Failed to load image")); };
    img.src = url;
  });
}

/**
 * Draw image onto canvas with target dimensions
 */
function drawToCanvas(img, targetW, targetH) {
  const canvas    = document.createElement("canvas");
  canvas.width    = targetW;
  canvas.height   = targetH;
  const ctx       = canvas.getContext("2d");
  // Fill white background so transparent PNGs don't go black when converted to JPEG
  ctx.fillStyle   = "#ffffff";
  ctx.fillRect(0, 0, targetW, targetH);
  ctx.drawImage(img, 0, 0, targetW, targetH);
  return canvas;
}

/**
 * Canvas → JPEG dataURL at given quality (0–1)
 */
function canvasToDataUrl(canvas, quality) {
  return canvas.toDataURL("image/jpeg", quality);
}

/**
 * DataURL → size in KB
 */
function dataUrlSizeKB(dataUrl) {
  // base64 string length * 0.75 gives approximate byte count
  const base64 = dataUrl.split(",")[1] || dataUrl;
  return Math.round((base64.length * 0.75) / 1024);
}

/**
 * Main export: compress + resize a File
 * Returns { dataUrl, sizeKB, originalSizeKB, compressed, width, height }
 */
export async function compressImage(file, options = {}) {
  const opts = { ...DEFAULT_OPTIONS, ...options };

  const img     = await loadImage(file);
  const origW   = img.naturalWidth;
  const origH   = img.naturalHeight;
  const origKB  = Math.round(file.size / 1024);

  /* ── Calculate target dimensions (maintain aspect ratio) ── */
  let targetW = origW;
  let targetH = origH;

  if (origW > opts.maxWidth || origH > opts.maxHeight) {
    const ratio = Math.min(opts.maxWidth / origW, opts.maxHeight / origH);
    targetW     = Math.round(origW * ratio);
    targetH     = Math.round(origH * ratio);
  }

  const canvas = drawToCanvas(img, targetW, targetH);

  /* ── Fast path: if original is already small enough, just resize ── */
  const firstTry    = canvasToDataUrl(canvas, opts.startQuality);
  const firstSizeKB = dataUrlSizeKB(firstTry);

  if (firstSizeKB <= opts.targetSizeKB) {
    return {
      dataUrl:       firstTry,
      sizeKB:        firstSizeKB,
      originalSizeKB: origKB,
      compressed:    origKB > firstSizeKB,
      width:         targetW,
      height:        targetH,
    };
  }

  /* ── Binary search for quality that hits targetSizeKB ── */
  let lo      = opts.minQuality;
  let hi      = opts.startQuality;
  let best    = firstTry;
  let bestKB  = firstSizeKB;
  let iterations = 0;
  const MAX_ITER = 8; // max 8 iterations is plenty

  while (lo <= hi && iterations < MAX_ITER) {
    const mid      = (lo + hi) / 2;
    const attempt  = canvasToDataUrl(canvas, mid);
    const sizeKB   = dataUrlSizeKB(attempt);

    if (sizeKB <= opts.targetSizeKB) {
      best   = attempt;
      bestKB = sizeKB;
      lo     = mid + 0.05; // try slightly higher quality
    } else {
      hi = mid - 0.05;     // need lower quality
    }
    iterations++;
  }

  /* ── If still above maxSizeKB, try an extra dimension reduction ── */
  if (bestKB > opts.maxSizeKB) {
    const reducedW  = Math.round(targetW * 0.7);
    const reducedH  = Math.round(targetH * 0.7);
    const canvas2   = drawToCanvas(img, reducedW, reducedH);
    const reduced   = canvasToDataUrl(canvas2, opts.minQuality);
    const reducedKB = dataUrlSizeKB(reduced);

    if (reducedKB < bestKB) {
      best   = reduced;
      bestKB = reducedKB;
    }
  }

  return {
    dataUrl:        best,
    sizeKB:         bestKB,
    originalSizeKB: origKB,
    compressed:     true,
    width:          targetW,
    height:         targetH,
  };
}

/**
 * Full photo handler — validates, compresses, returns dataUrl + info
 * Throws descriptive Error on validation failure
 */
export async function processPhoto(file) {
  if (!file) throw new Error("No file provided");

  const allowed = ["image/jpeg", "image/jpg", "image/png", "image/webp"];
  if (!allowed.includes(file.type)) {
    throw new Error("Please upload a JPG, PNG, or WebP image");
  }

  // 10MB raw limit — we'll compress it down
  if (file.size > 10 * 1024 * 1024) {
    throw new Error("Image is too large (max 10MB). Please choose a smaller file.");
  }

  const result = await compressImage(file, {
    maxWidth:     800,
    maxHeight:    800,
    targetSizeKB: 150,
    maxSizeKB:    200,
    startQuality: 0.85,
    minQuality:   0.30,
  });

  return result;
}
